import axios from "axios";
import eventBus from "./EventBus";

const url = "https://cognito-idp.us-west-1.amazonaws.com/";
const isIosWebKit = /iP(ad|hone|od).+WebKit/i.test(navigator.userAgent);

export const login = async (email, password) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_TNFT_SERVER}/auth/adminlogin`,
      { email, password },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = response.data;

    if (!data.success) {
      // Server responded but login failed
      return { success: false, error: data.error || "Login failed" };
    }

    if (data.newPassword) {
      // Need to complete new password challenge
      return { success: true, newPassword: true, session: data.session };
    }

    localStorage.setItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`,
      data.accessToken
    );

    localStorage.setItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`,
      "true"
    );

    const { requireMessageAck, requireTermsAck } = data || {};
    if (requireMessageAck || requireTermsAck) {
       if (requireMessageAck) localStorage.setItem('requireMessageAck', 'true');
       if (requireTermsAck) localStorage.setItem('requireTermsAck', 'true');
        eventBus.emit("SHOW_SYSTEM_MESSAGE", {
            requireMessageAck,
            requireTermsAck,
          });
    }

    return { success: true };
  } catch (e) {
    console.error(
      "Error logging in via server:",
      e.response?.data || e.message
    );
    // Clean up any stored flags
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
    );
    const errMsg = e.response?.data?.error || e.message;
    return { success: false, error: errMsg };
  }
};

export const completeNewPassword = async (username, newPassword, session) => {
  try {
    const headers = {
      "X-Amz-Target":
        "AWSCognitoIdentityProviderService.RespondToAuthChallenge",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      ChallengeResponses: {
        USERNAME: username,
        NEW_PASSWORD: newPassword,
      },
      Session: session,
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Complete new password success");
      //verify email
      try {
        const apiHeaders = {
          Accesstoken: result.data.AuthenticationResult.AccessToken,
        };
        const apiResult = await axios.post(
          `${process.env.REACT_APP_TNFT_SERVER}/users/verifyemail`,
          body,
          { headers: apiHeaders }
        );

        await login(username, newPassword);
      } catch (e) {
        var error = `Error validating email. Error: ${e.message}`;
        console.error(error);
        return { success: false, error };
      }

      return { success: true, data: result.data };
    } else {
      return { success: false, error: "Unexpected response" };
    }
  } catch (e) {
    let errorMsg = e?.response?.data?.message || e.message;
    console.error("Error completing new password:", errorMsg);
    return { success: false, error: errorMsg };
  }
};

export const confirm = async (email, code) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmSignUp",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: email,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      ConfirmationCode: code,
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirm success");
      return { success: true };
    }
  } catch (e) {
    if (e?.response?.data?.__type === "CodeMismatchException") {
      return { success: false, error: "Invalid code. Please try again" };
    }
    error = `Error signing confirming user. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const validateEmailCode = async (code, email) => {
  let error;
  try {
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    if (!token) {
      localStorage.removeItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
      );
      return { success: false };
    }

    const headers = {
      "X-Amz-Target": `AWSCognitoIdentityProviderService.VerifyUserAttribute`,
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      AccessToken: token,
      AttributeName: "email",
      Code: code,
    };
    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Code confirmed");
      return { success: true };
    } else {
      return { success: false };
    }
  } catch (e) {
    error = `Error updating email. Error: ${e.message}`;
    console.error(error);
    return { success: false, error };
  }
};

export const forgot = async (email) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ForgotPassword",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: email,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Forgot success");
      return { success: true };
    }
  } catch (e) {
    error = e?.response?.data?.message;
    console.error(error);
    return { success: false, error };
  }
};

export const confirmForgot = async (email, password, code) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmForgotPassword",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: email,
      ConfirmationCode: code,
      Password: password,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirm forgot success");
      const loginResult = await login(email, password);
      return loginResult;
    }
  } catch (e) {
    if (e?.response?.data?.__type == "CodeMismatchException") {
      error = "Invalid code. Please try again.";
    } else {
      error = `Error resetting password.${e?.response?.data?.message}`;
    }
    console.error(error);
    return { success: false, error };
  }
};

export const resendCode = async (email) => {
  let error;
  try {
    const headers = {
      "X-Amz-Target":
        "AWSCognitoIdentityProviderService.ResendConfirmationCode",
      "Content-Type": "application/x-amz-json-1.1",
    };

    const body = {
      Username: email,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    };

    const result = await axios.post(url, body, { headers });
    if (result?.status === 200) {
      console.log("Confirmation code resent successfully");
      return { success: true };
    }
  } catch (e) {
    error =
      e?.response?.data?.message ||
      `Error resending confirmation code: ${e.message}`;
    console.error(error);
    alert(error); // Optionally notify the user
    return { success: false, error };
  }
};

export const validateEmail = async (email) => {
  try {
    const body = { email };
    const headers = { "Content-Type": "application/json" };

    const result = await axios.post(
      `${process.env.REACT_APP_TNFT_SERVER}/emailvalidator`,
      body,
      { headers }
    );

    return result.data;
  } catch (e) {
    var error = `Error validating email. Error: ${e.message}`;
    console.error(error);
    return { success: true };
  }
};

export const logout = async () => {
  try {
    const accesstoken = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    localStorage.removeItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
    );
    const result = await axios.post(
      `${process.env.REACT_APP_TNFT_SERVER}/auth/adminlogout`,
      {},
      {
        headers: { "Content-Type": "application/json", accesstoken },
      }
    );
    return;
  } catch (e) {
    console.error("Error logging out", e.response?.data || e.message);
    const errMsg = e.response?.data?.error || e.message;
    return { success: false, error: errMsg };
  }
};
